import React from 'react';
import { useUser } from '../context/UserContext';
import { useFirebaseAuthReady } from '../hooks/useFirebaseAuthReady';
import { useTheme } from '../context/ThemeProvider';
import Login from '../app/login/page';
import Signup from '../app/signup/page';
import EmployeeSignup from '../app/employee-signup/[employeeRegistrationCode]/page';
import ResetPassword from '../app/reset-password/page';
import Home from '../app/home/page';
import Settings from '../app/settings/page';
import PrivacyHub from '../app/privacy-hub/page';
import Document from '../app/document/[documentId]/page';
import ChangelogPage from '../app/changelog/page';
import User from '../app/user/[userId]/page';
import Company from '../app/company/[companyId]/page';
import Budget from '../app/project/[projectId]/page';
import KnowledgePage from '../app/knowledge/[knowledgeId]/page';
import CvCheck from '../app/cv-check/page';
import ThesconAi from '../app/thescon-ai/page';
import DocumentationPage from '../app/documentation/page';
import { Routes, Route, Navigate } from 'react-router-dom';

interface AuthViewProps {
  children?: React.ReactNode;
}

export default function AuthView({ children }: AuthViewProps) {
  const { user, loading } = useUser();
  const isAuthReady = useFirebaseAuthReady();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Show loading spinner while Firebase auth is initializing
  if (!isAuthReady || loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px'
      }}>
        <img src={isDarkMode ? "/assets/graphics/thescon-loading-white.svg" : "/assets/graphics/thescon-loading.svg"} alt="Loading" width={120} height={120} />
      </div>
    );
  }

  // If user is not authenticated, only show auth-related routes
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/employee-signup" element={<EmployeeSignup />} />
        <Route path="/employee-signup/:employeeRegistrationCode" element={<EmployeeSignup />} />
        <Route path="/reset-password" element={<ResetPassword />} />
         <Route path="/changelog" element={<ChangelogPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // If user is authenticated, show protected routes
  return (
    <Routes>
      <Route path="/home" element={<Home />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/privacy-hub" element={<PrivacyHub />} />
      <Route path="/changelog" element={<ChangelogPage />} />
      <Route path="/document/:documentId" element={<Document />} />
      <Route path="/user/:userId" element={<User />} />
      <Route path="/company/:companyId" element={<Company />} />
      <Route path="/project/:projectId" element={<Budget />} />
      <Route path="/budget/:budgetId" element={<Budget />} />
      <Route path="/knowledge/:knowledgeId" element={<KnowledgePage />} />
      <Route path="/cv-check" element={<CvCheck />} />
      <Route path="/cv-check/:cvCheckId" element={<CvCheck />} />
      <Route path="/thescon-ai" element={<ThesconAi />} />
      <Route path="/documentation" element={<DocumentationPage />} />
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}
